import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import { Row, Col, Form, Button } from "react-bootstrap";
import * as Yup from "yup";
import { toast } from "react-toastify";
import useApiClient, { apiRoutes } from "../../../../hooks/apiClient";
import TelaAtividadeProrrogacao from "./TelaAtividadeProrrogacao";
import FormAlert from "../../../../components/UI/FormAlert/FormAlert";
import useAssinarAtividade from "../hooks/useAssinarAtividade";
import useExcluirAtividade from "../hooks/useExcluirAtividade";

const retornaDataToNumero = (data) => {
  const objData = new Date(data);
  const retornaApenasData = new Date(objData.getFullYear(), objData.getMonth(), objData.getDate(), 0, 0, 0);
  return Date.parse(retornaApenasData) || 0;
};

const validationSchema = Yup.object().shape({
  textoDocumento: Yup.string()
    .default("")
    .test("texto-doc", "Digite o texto da atividade", (value, atividade) => {
      if (atividade.parent.siglaAtividade === "P4") {
        if ((value || []).length === 0 || value === undefined || value === null || value === "<p><br></p>")
          return false;
      }
      return true;
    }),
  sequencialAtividade: Yup.string().default(),
  sequencialPedidoProrrogacao: Yup.string().default(),
  sequencialAndamento: Yup.string().default(),
  siglaAtividade: Yup.string().default(""),
  situacaoUltimoAndamento: Yup.string().default(),
  cpfRepresentante: Yup.string()
    .default("")
    .test("cpf-valido", "Digite CPF válido", (value, atividade) => {
      if (
        atividade.parent.siglaAtividade === "P6" ||
        atividade.parent.siglaAtividade === "P12" ||
        atividade.parent.siglaAtividade === "P13"
      ) {
        if ((value || []).length !== 14) return false;
      }
      return true;
    }),
  nomeRepresentante: Yup.string()
    .default("")
    .test("nome-valido", "Digite nome do representante", (value, atividade) => {
      if (
        atividade.parent.siglaAtividade === "P6" ||
        atividade.parent.siglaAtividade === "P12" ||
        atividade.parent.siglaAtividade === "P13"
      ) {
        if ((value || []).length === 0) return false;
      }

      return true;
    }),
  cargoRepresentante: Yup.string()
    .default("")
    .test("cargo-valido", "Digite cargo do representante", (value, atividade) => {
      if (
        atividade.parent.siglaAtividade === "P6" ||
        atividade.parent.siglaAtividade === "P12" ||
        atividade.parent.siglaAtividade === "P13"
      ) {
        if ((value || []).length === 0) return false;
      }
      return true;
    }),
  nuPaginaEdital: Yup.string()
    .default("")
    .test("numero-valido", "Digite Nº do Diário Oficial", (value, atividade) => {
      if (atividade.parent.siglaAtividade === "P10") {
        if ((value || []).length === 0 || value === undefined) return false;
      }
      return true;
    }),

  dataComunicado: Yup.date().test(
    "data-valida",
    "Data cadastrada não pode ser nula ou posterior a atual.",
    (value, atividade) => {
      if (
        retornaDataToNumero(value) > retornaDataToNumero(new Date()) &&
        (atividade.parent.siglaAtividade === "P6" ||
          atividade.parent.siglaAtividade === "P10" ||
          atividade.parent.siglaAtividade === "P12")
      ) {
        return false;
      }
      return true;
    },
  ),
  codigoRastreio: Yup.string()
    .default("")
    .test("codigo-valido", "Digite o código de rastreio", (value, atividade) => {
      if (
        atividade.parent.siglaAtividade === "P11" &&
        ((value || []).length === 0 || value === undefined || value === null)
      )
        return false;

      return true;
    }),
});

const initialValues = {};
validationSchema._nodes.forEach((key) => {
  initialValues[key] = "";
});

const TelaProximaAtividadeProrrogacao = ({ dadosOs, data, atividade }) => {
  const { request: salvarRequest, data: salvarData, errors: salvarErrors, isLoading: salvarIsLoading } = useApiClient();
  const { request: requestProrrog, data: dataProrrog } = useApiClient();
  const [dataAndamento, setDataAndamento] = useState(data);

  // Se situacaoUltimoAndamento é 'A' significa que a atividade está em andamento,
  // portanto, o sequencialAndamento a ser usado é o que vem no result.
  // Caso a situação seja G, significa que nova atividade será feita, então
  // o sequencialAndamento ainda será incluído, portanto será enviado valor 0.
  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: (values) => {
      const parametros = {
        sequencialPedidoProrrogacao: values.sequencialPedidoProrrogacao,
        sequencialAcaoFiscal: dadosOs?.acaoFiscal,
        sequencialOrdemServico: dadosOs?.sequencial,
        sequencialAtividade: atividade?.sequencialAtividade,
        sequencialAndamento: values.sequencialAndamento,
        textoDocumento: values.textoDocumento,
        cpfRepresentante: values?.cpfRepresentante,
        nomeRepresentante: values?.nomeRepresentante,
        cargoRepresentante: values?.cargoRepresentante,
        nuPaginaEdital: values?.nuPaginaEdital,
        codigoRastreio: values?.codigoRastreio,
        dataComunicado: values?.dataComunicado,
      };

      if (dataAndamento.situacaoUltimoAndamento === "A") {
        salvarRequest(apiRoutes.fiscalizacao.prorrogacaoos.alterarAtividadeProrrogacao, parametros);
      } else {
        salvarRequest(apiRoutes.fiscalizacao.prorrogacaoos.incluirAtividadeProrrogacao, parametros);
      }
    },
  });

  const { excluirAtividade, excluirErrors, excluirIsLoading } = useExcluirAtividade({ dataAndamento });

  const { assinarAtividade, assinarIsLoading, assinarErrors } = useAssinarAtividade({
    dadosOs,
    dataAndamento,
    atividade,
    formik,
  });

  const { setValues, setSubmitting } = formik;

  useEffect(() => {
    if (salvarData) {
      toast.success("Atividade salva com sucesso!");

      const params = {
        sqPedidoProrrogacao: data.sqPedidoProrrogacao,
        sqAcaoFiscal: dadosOs.acaoFiscal,
      };

      requestProrrog(apiRoutes.fiscalizacao.prorrogacaoos.ultimaatividadeprorrogacao, params);
    }

    setSubmitting(false);
  }, [salvarData, setSubmitting, dadosOs, data, requestProrrog]);

  useEffect(() => {
    if (dataProrrog) {
      setDataAndamento(dataProrrog.result[0]);
    } else {
      setDataAndamento(data);
    }
  }, [dataProrrog, data]);

  useEffect(() => {
    const texto =
      dataAndamento?.situacaoUltimoAndamento === "A"
        ? dataAndamento?.txDocumentoUltimoAndamento
        : atividade?.textoDocumento;
    const sequencialPedidoProrrogacao = dataAndamento?.sqPedidoProrrogacao
      ? dataAndamento?.sqPedidoProrrogacao
      : data.sqPedidoProrrogacao;
    const sequencialAndamento = dataAndamento?.sequencialUltimoAndamento;
    const situacaoUltimoAndamento = dataAndamento?.situacaoUltimoAndamento;
    const siglaAtividade = atividade?.siglaAtividade ? atividade?.siglaAtividade : "";
    const cpfRepresentante = dataAndamento?.cpfRepresentante ? dataAndamento?.cpfRepresentante : "";
    const nomeRepresentante = dataAndamento?.nomeRepresentante ? dataAndamento?.nomeRepresentante : "";
    const cargoRepresentante = dataAndamento?.cargoRepresentante ? dataAndamento?.cargoRepresentante : "";
    const nuPaginaEdital = dataAndamento?.nuPaginaEdital ? dataAndamento?.nuPaginaEdital : " ";
    let dataComunicado = dataAndamento?.dataComunicado ? dataAndamento?.dataComunicado : new Date().toISOString();
    const codigoRastreio = dataAndamento?.codigoRastreio ? dataAndamento?.codigoRastreio : "";

    const dataCiencia = dataAndamento?.dataCiencia ? dataAndamento?.dataCiencia : new Date().toISOString();

    if (atividade?.siglaAtividade === "P15" && dataAndamento.situacaoUltimoAndamento !== "A") {
      dataComunicado = dataCiencia;
    }

    setValues((curr) => ({
      ...curr,
      textoDocumento: texto,
      sequencialPedidoProrrogacao,
      sequencialAndamento,
      situacaoUltimoAndamento,
      siglaAtividade,
      cpfRepresentante,
      nomeRepresentante,
      cargoRepresentante,
      nuPaginaEdital,
      codigoRastreio,
      dataComunicado,
    }));
  }, [
    setValues,
    atividade?.textoDocumento,
    dataAndamento?.sqPedidoProrrogacao,
    dataAndamento?.sequencialUltimoAndamento,
    dataAndamento?.situacaoUltimoAndamento,
    dataAndamento?.txDocumentoUltimoAndamento,
    atividade?.siglaAtividade,
    dataAndamento?.cpfRepresentante,
    dataAndamento?.nomeRepresentante,
    dataAndamento?.cargoRepresentante,
    dataAndamento?.nuPaginaEdital,
    dataAndamento?.codigoRastreio,
    dataAndamento?.dataComunicado,
    dataAndamento?.dataCiencia,
    data,
  ]);

  if (!atividade?.sequencialAtividade) {
    return null;
  }

  return (
    <>
      <FormAlert
        formik={formik}
        data={salvarData}
        errors={salvarErrors || excluirErrors || assinarErrors}
        isLoading={salvarIsLoading || excluirIsLoading || assinarIsLoading}
        dismissible
      />
      <Form noValidate onSubmit={formik.handleSubmit}>
        <Row className="mt-2">
          <Col>
            <Row className="text-right">
              <Col>
                <Button variant="primary" className="mr-3" onClick={formik.handleSubmit}>
                  Salvar
                </Button>
                <Button
                  variant="primary"
                  className="mr-3"
                  disabled={!formik.isValid}
                  onClick={() => {
                    assinarAtividade();
                  }}
                >
                  Assinar
                </Button>
                {dataAndamento?.situacaoUltimoAndamento === "A" && (
                  <Button variant="primary" className="mr-3" onClick={() => excluirAtividade()}>
                    Excluir
                  </Button>
                )}
              </Col>
            </Row>
            <Row className="mt-2">
              <Col>
                <h5>{atividade.nomeAtividade}</h5>
              </Col>
            </Row>

            <TelaAtividadeProrrogacao formik={formik} dadosOs={dadosOs} atividade={atividade} />
          </Col>
        </Row>
      </Form>
    </>
  );
};

export default TelaProximaAtividadeProrrogacao;
